// Web Push in the browser: turning this device's notifications on and off.
// The service worker (public/sw.js) shows them; the `push` Edge Function sends
// them. Only the hosted web app (and the installed phone app) can do this.
import { desktopBridge } from './bridge';

export type PushSupport =
  | 'ok'
  /** No service worker or Push API (or the desktop app, which has its own notifications). */
  | 'unsupported'
  /** iPhone or iPad in Safari: push only works from the home-screen app. */
  | 'install-first'
  /** VITE_VAPID_PUBLIC_KEY isn't set for this build. */
  | 'not-configured';

export function pushSupport(env: {
  vapidKey: string | undefined;
  hasServiceWorker: boolean;
  hasPushManager: boolean;
  desktop: boolean;
  userAgent: string;
  standalone: boolean;
}): PushSupport {
  if (env.desktop) return 'unsupported';
  const ios = /iPhone|iPad|iPod/.test(env.userAgent);
  if (ios && !env.standalone) return 'install-first';
  if (!env.hasServiceWorker || !env.hasPushManager) return 'unsupported';
  if (!env.vapidKey) return 'not-configured';
  return 'ok';
}

export function browserPushSupport(vapidKey: string | undefined): PushSupport {
  return pushSupport({
    vapidKey,
    hasServiceWorker: typeof navigator !== 'undefined' && 'serviceWorker' in navigator,
    hasPushManager: typeof window !== 'undefined' && 'PushManager' in window,
    desktop: !!desktopBridge(),
    userAgent: typeof navigator === 'undefined' ? '' : navigator.userAgent,
    standalone:
      typeof window !== 'undefined' &&
      (window.matchMedia?.('(display-mode: standalone)').matches ||
        (navigator as Navigator & { standalone?: boolean }).standalone === true),
  });
}

/** "Chrome on Android", for the device list. */
export function deviceName(userAgent: string): string {
  const os = /iPhone/.test(userAgent)
    ? 'iPhone'
    : /iPad/.test(userAgent)
      ? 'iPad'
      : /Android/.test(userAgent)
        ? 'Android'
        : /Windows/.test(userAgent)
          ? 'Windows'
          : /Mac OS X|Macintosh/.test(userAgent)
            ? 'Mac'
            : /Linux/.test(userAgent)
              ? 'Linux'
              : 'this device';
  const browser = /Edg\//.test(userAgent)
    ? 'Edge'
    : /Firefox\//.test(userAgent)
      ? 'Firefox'
      : /Chrome\//.test(userAgent)
        ? 'Chrome'
        : /Safari\//.test(userAgent)
          ? 'Safari'
          : 'Browser';
  return `${browser} on ${os}`;
}

/** The VAPID public key (URL-safe base64) as the bytes PushManager wants. */
export function keyBytes(base64Url: string): Uint8Array<ArrayBuffer> {
  const base64 = (base64Url + '='.repeat((4 - (base64Url.length % 4)) % 4)).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const bytes = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i);
  return bytes;
}

export interface DeviceKeys {
  endpoint: string;
  p256dh: string;
  auth: string;
}

/** Asks permission (if needed) and subscribes this browser. Throws with a reason people can act on. */
export async function subscribeThisDevice(vapidKey: string): Promise<DeviceKeys> {
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    throw new Error('Notifications are blocked for Hive on this device. Allow them in the browser or phone settings.');
  }
  const registration = await navigator.serviceWorker.ready;
  const existing = await registration.pushManager.getSubscription();
  const subscription =
    existing ??
    (await registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes(vapidKey) }));
  const json = subscription.toJSON() as { endpoint?: string; keys?: { p256dh?: string; auth?: string } };
  if (!json.endpoint || !json.keys?.p256dh || !json.keys.auth)
    throw new Error('The browser gave an incomplete subscription.');
  return { endpoint: json.endpoint, p256dh: json.keys.p256dh, auth: json.keys.auth };
}

/** This browser's push address, if it's subscribed. */
export async function thisDeviceEndpoint(): Promise<string | null> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return null;
  const registration = await navigator.serviceWorker.getRegistration();
  return (await registration?.pushManager.getSubscription())?.endpoint ?? null;
}

export async function unsubscribeThisDevice(): Promise<void> {
  const registration = await navigator.serviceWorker.getRegistration();
  await (await registration?.pushManager.getSubscription())?.unsubscribe();
}
